import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { isPortalRole, isAdminRole, homeRouteForUser } from "@/lib/roleUtils";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/layout/AppLayout";
import RouteSkeleton from "@/components/layout/RouteSkeleton";
import { Toaster } from "sonner";

// Phase 1.18 — route-based code splitting. Login stays eager because it's the
// entry route for unauthenticated users; every heavy page below is lazy.
import Login from "@/pages/Login";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Customers = lazy(() => import("@/pages/Customers"));
const CustomerFlowCreate = lazy(() => import("@/pages/CustomerFlowCreate"));
const CustomerCreate = lazy(() => import("@/pages/CustomerCreate"));
const CustomerImport = lazy(() => import("@/pages/CustomerImport"));
const BulkSms = lazy(() => import("@/pages/BulkSms"));
const CustomerDetail = lazy(() => import("@/pages/CustomerDetail"));
const Requests = lazy(() => import("@/pages/Requests"));
const RequestDetail = lazy(() => import("@/pages/RequestDetail"));
const Appointments = lazy(() => import("@/pages/Appointments"));
const DailyBoard = lazy(() => import("@/pages/DailyBoard"));
const Settings = lazy(() => import("@/pages/Settings"));
const ContractTemplatesSettings = lazy(() => import("@/pages/ContractTemplatesSettings"));
const ContractsArchive = lazy(() => import("@/pages/ContractsArchive"));
const ConsentPublic = lazy(() => import("@/pages/ConsentPublic"));
const ConsentRecord = lazy(() => import("@/pages/ConsentRecord"));
const AftercarePublic = lazy(() => import("@/pages/AftercarePublic"));
const SatisfactionPublic = lazy(() => import("@/pages/SatisfactionPublic"));
const OnboardingPublic = lazy(() => import("@/pages/OnboardingPublic"));
const AppointmentContractPublic = lazy(() => import("@/pages/AppointmentContractPublic"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Satisfactions = lazy(() => import("@/pages/Satisfactions"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Segments = lazy(() => import("@/pages/Segments"));
const Imports = lazy(() => import("@/pages/Imports"));
const Reports = lazy(() => import("@/pages/Reports"));
const Artists = lazy(() => import("@/pages/Artists"));
const ArtistDetail = lazy(() => import("@/pages/ArtistDetail"));
const Payouts = lazy(() => import("@/pages/Payouts"));
const CompletionApprovals = lazy(() => import("@/pages/CompletionApprovals"));
const CashRegister = lazy(() => import("@/pages/CashRegister"));
const Unassigned = lazy(() => import("@/pages/Unassigned"));
const ProductSales = lazy(() => import("@/pages/ProductSales"));
const TeamShift = lazy(() => import("@/pages/TeamShift"));

const PortalLayout = lazy(() => import("@/pages/portal/PortalLayout"));
const PortalHome = lazy(() => import("@/pages/portal/PortalHome"));
const PortalAppointments = lazy(() => import("@/pages/portal/PortalAppointments"));
const PortalAppointmentNew = lazy(() => import("@/pages/portal/PortalAppointmentNew"));
const PortalAppointmentDetail = lazy(() => import("@/pages/portal/PortalAppointmentDetail"));
const PortalEarnings = lazy(() => import("@/pages/portal/PortalEarnings"));
const PortalSchedule = lazy(() => import("@/pages/portal/PortalSchedule"));
const PortalTeamShift = lazy(() => import("@/pages/portal/PortalTeamShift"));

function LoginGuard({ children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (user) {
    // Phase 1.39E — Role-based routing. Manager (login_type=personnel for
    // scoping) MUST land in admin panel, not portal.
    return <Navigate to={homeRouteForUser(user)} replace />;
  }
  return children;
}

function ArtistRedirect({ children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (isPortalRole(user)) return <Navigate to="/personel-portal" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Phase 1.39E — AdminOnly = admin/super_admin ONLY (manager is management
  // but not admin). Anything more permissive would leak destructive actions.
  if (!isAdminRole(user)) return <Navigate to="/" replace />;
  return children;
}

// Legacy /artist-portal/* → /personel-portal/* client-side redirect (preserves subpath + query)
function LegacyPortalRedirect() {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/artist-portal/, "/personel-portal");
  return <Navigate to={`${newPath}${location.search}${location.hash}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            classNames: {
              toast: "bg-[#111] border border-white/10 text-white",
              title: "font-display uppercase tracking-wider",
              description: "text-neutral-400",
            },
          }}
        />
        <Suspense fallback={<RouteSkeleton />}>
          <Routes>
            <Route path="/login" element={<LoginGuard><Login /></LoginGuard>} />
            <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
            <Route path="/sifre-sifirla" element={<ResetPassword />} />
            <Route path="/consent/:token" element={<ConsentPublic />} />
            <Route path="/feedback/aftercare/:token" element={<AftercarePublic />} />
            <Route path="/feedback/satisfaction/:token" element={<SatisfactionPublic />} />
            <Route path="/onboarding/:token" element={<OnboardingPublic />} />
            <Route path="/randevu-onay/:token" element={<AppointmentContractPublic />} />
            <Route element={<ProtectedRoute><ArtistRedirect><AppLayout /></ArtistRedirect></ProtectedRoute>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customers/new" element={<CustomerCreate />} />
              <Route path="/customers/import" element={<CustomerImport />} />
              <Route path="/musteri-yeni" element={<CustomerFlowCreate />} />
              <Route path="/customers/:id" element={<CustomerDetail />} />
              <Route path="/toplu-sms" element={<BulkSms />} />
              <Route path="/requests" element={<Requests />} />
              <Route path="/requests/:id" element={<RequestDetail />} />
              <Route path="/appointments" element={<DailyBoard />} />
              <Route path="/appointments/list" element={<Appointments />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/contracts" element={<AdminOnly><ContractTemplatesSettings /></AdminOnly>} />
              <Route path="/contracts/archive" element={<AdminOnly><ContractsArchive /></AdminOnly>} />
              <Route path="/satisfactions" element={<Satisfactions />} />
              <Route path="/audit" element={<AuditLog />} />
              <Route path="/consents/:id" element={<ConsentRecord />} />
              <Route path="/segments" element={<Segments />} />
              <Route path="/import" element={<Imports />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/artists" element={<Artists />} />
              {/* Phase 1.32 — canonical alias so links to /personnel resolve. */}
              <Route path="/personnel" element={<Navigate to="/artists" replace />} />
              <Route path="/artists/:id" element={<ArtistDetail />} />
              <Route path="/payouts" element={<Payouts />} />
              <Route path="/completion-approvals" element={<AdminOnly><CompletionApprovals /></AdminOnly>} />
              <Route path="/cash-register" element={<CashRegister />} />
              <Route path="/unassigned" element={<AdminOnly><Unassigned /></AdminOnly>} />
              <Route path="/urun-satisi" element={<ProductSales />} />
              <Route path="/team-shifts" element={<AdminOnly><TeamShift /></AdminOnly>} />
            </Route>
            <Route path="/personel-portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
              <Route index element={<PortalHome />} />
              <Route path="appointments" element={<PortalAppointments />} />
              <Route path="appointments/:id" element={<PortalAppointmentDetail />} />
              <Route path="new" element={<PortalAppointmentNew />} />
              <Route path="earnings" element={<PortalEarnings />} />
              <Route path="schedule" element={<PortalSchedule />} />
              <Route path="team-shifts" element={<PortalTeamShift />} />
            </Route>
            {/* Legacy /artist-portal/* → /personel-portal/* (subpath + query preserved) */}
            <Route path="/artist-portal" element={<LegacyPortalRedirect />} />
            <Route path="/artist-portal/*" element={<LegacyPortalRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
